import React, { useEffect, useState } from "react";
import MainLayout from "../../../../layouts/MainLayout";
import { useNavigate, useParams } from "react-router-dom";
import "../../../../assets/styles/custom-styles.css";
import useForm from "../../../../hooks/useForm";
import { DetailsOsItemFields } from "../../../../constants/forms/osItemFields";
import { setDefaultFieldValues } from "../../../../utils/objectUtils";
import useBaseService from "../../../../hooks/services/useBaseService";
import useLoader from "../../../../hooks/useLoader";
import useNotification from "../../../../hooks/useNotification";
import DetailsSectionRenderer from "../../../../components/DetailsSectionRenderer";
import PageHeader from "../../../../components/PageHeader";
import { entities } from "../../../../constants/entities";
import { useSectionFields } from "../../../../hooks/useSectionFields";
import useQueryWithLoader from "../../../../hooks/useQueryWithLoader";

const DetailsItemOrderServicePage = () => {
  const { osId, itemId } = useParams();
  const navigate = useNavigate();
  const { showLoader, hideLoader } = useLoader();
  const { showNotification } = useNotification();
  const { getByColumn: baseServiceGetByColumn } = useBaseService(navigate);
  const [sectionFields, setSectionFields] = useState(DetailsOsItemFields);
  const { removeFields } = useSectionFields();

  // Inicializa formData com os valores padrão definidos em DetailsOsItemFields
  const { formData, setFormData } = useForm(
    setDefaultFieldValues(DetailsOsItemFields),
  );

  // --- Query 1: Buscar os detalhes do Item da Ordem de Serviço ---
  const {
    data: osItemDetails,
    isLoading: isLoadingOsItemDetails,
    isFetching: isFetchingOsItemDetails,
    isError: isErrorOsItemDetails,
    error: osItemDetailsError,
  } = useQueryWithLoader({
    queryKey: ["osItemDetails", osId, itemId],
    queryFn: async () => {
      const response = await baseServiceGetByColumn(
        entities.serviceOrders.serviceOrderItem(osId, itemId),
      );
      return response.result;
    },
    enabled: !!osId && !!itemId,
    staleTime: 5 * 60 * 1000,
  });

  // --- Efeito para atualizar o formData quando os detalhes do item da OS estiverem disponíveis ---
  useEffect(() => {
    if (
      !isLoadingOsItemDetails &&
      !isErrorOsItemDetails &&
      !osItemDetailsError &&
      osItemDetails
    ) {
      setFormData({
        address: osItemDetails?.address
          ? `${osItemDetails?.address?.alias}, ${osItemDetails?.address?.street}, ${osItemDetails?.address?.number} - ${osItemDetails?.address?.city} - ${osItemDetails?.address?.state}`
          : "-",
        details: osItemDetails.details || "-",
        location: osItemDetails?.location
          ? `${osItemDetails?.location?.area}, ${osItemDetails?.location?.section}, ${osItemDetails?.location?.spot}`
          : "-",
        department: osItemDetails.department?.name || "-",
        movementType: osItemDetails.movement_type?.name || "-",
        contract_item:
          osItemDetails.contract_item?.alias ||
          osItemDetails.contract_item?.description ||
          (osItemDetails.product_id || osItemDetails.product
            ? "Insumos"
            : osItemDetails.type?.is_kit ||
                osItemDetails.equipment?.type?.is_kit ||
                osItemDetails.extra_equipments?.length
              ? "Kits"
              : "-"),
        quantity: osItemDetails.quantity || 0,
        product: osItemDetails.product?.name || "-",
        quantity_product: osItemDetails.quantity_product || 0,
      });
    }

    if (isErrorOsItemDetails && osItemDetailsError) {
      showNotification(
        "error",
        `Erro ao carregar detalhes do item da OS: ${osItemDetailsError.message}`,
      );
      navigate(`/aluguel/requisicoes/${osId}/detalhes`, {
        replace: true,
        state: { type: "error", message: "Item da OS não encontrado." },
      });
    }
  }, [
    osItemDetails,
    setFormData,
    isLoadingOsItemDetails,
    isErrorOsItemDetails,
    osItemDetailsError,
    showNotification,
    navigate,
    osId,
  ]);

  // UseEffect para remover os campos com base em produtos e equipamentos, se for equipamento o campo de produto deve ser removido e vice versa
  useEffect(() => {
    if (!osItemDetails) return;
    let updatedFields = sectionFields;

    if (!osItemDetails.contract_item_id) {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: "contract_item",
      });
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: "quantity",
      });
    }

    if (!osItemDetails.product) {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: "product",
      });
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: "quantity_product",
      });
    }

    setSectionFields(updatedFields);
  }, [osItemDetails, removeFields, sectionFields]);

  const isPageLoading = isLoadingOsItemDetails || isFetchingOsItemDetails;

  useEffect(() => {
    if (isPageLoading) {
      showLoader();
    } else {
      hideLoader();
    }
  }, [isPageLoading, showLoader, hideLoader]);

  // --- Notificação de Erro para a página ---
  useEffect(() => {
    if (isErrorOsItemDetails && osItemDetailsError) {
      showNotification(
        "error",
        `Erro ao carregar detalhes do item da OS: ${osItemDetailsError.message}`,
      );
    }
  }, [isErrorOsItemDetails, osItemDetailsError, showNotification]);

  // Considerar o estado de carregamento para a UI
  if (isPageLoading) {
    return (
      <MainLayout selectedCompany="ALUCOM">
        <PageHeader title="Carregando Detalhes..." showBackButton={true} />
        <div className="container-fluid p-1">
          <p>Carregando dados do item da Requisição...</p>
        </div>
      </MainLayout>
    );
  }

  if (isErrorOsItemDetails) {
    return (
      <MainLayout selectedCompany="ALUCOM">
        <PageHeader title="Erro ao Carregar" showBackButton={true} />
        <div className="container-fluid p-1">
          <p>
            Ocorreu um erro ao carregar os detalhes do item da Requisição. Por
            favor, tente novamente.
          </p>
        </div>
      </MainLayout>
    );
  }

  // Se os dados foram carregados mas `osItemData` ainda é nulo ou indefinido (ex: item não encontrado e sem erro claro da API)
  if (!osItemDetails) {
    return (
      <MainLayout selectedCompany="ALUCOM">
        <PageHeader title="Item Não Encontrado" showBackButton={true} />
        <div className="container-fluid p-1">
          <p>O item da Requisição não foi encontrado.</p>
        </div>
      </MainLayout>
    );
  }

  return (
    <MainLayout selectedCompany="ALUCOM">
      <PageHeader
        title="Detalhes do Item da Requisição"
        showBackButton={true}
      />
      <div className="container-fluid p-1">
        <DetailsSectionRenderer
          sections={sectionFields}
          formData={formData}
          readOnly
        />
      </div>
    </MainLayout>
  );
};

export default DetailsItemOrderServicePage;
